import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { useModules, useDeleteModule } from '../../../hooks/subscriptions';
import { useSnackbar } from '../../../components/SnackbarProvider';
import { getCategoryLabel, getCategoryColor } from '../../../utils/subscriptions';
import { ModuleDependenciesCell } from '../../../components/subscriptions/ModuleDependenciesCell';
import { ModuleDependentsCell } from '../../../components/subscriptions/ModuleDependentsCell';
import type { Module } from '../../../types/subscriptions';

export const ModuleList = () => {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState<string | null>(null);

  const { data: modules, isLoading } = useModules();
  const deleteMutation = useDeleteModule();

  const handleDeleteClick = (id: string) => {
    setModuleToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (moduleToDelete) {
      deleteMutation.mutate(moduleToDelete, {
        onSuccess: () => {
          showMessage('Module deleted successfully', 'success');
          setDeleteDialogOpen(false);
          setModuleToDelete(null);
        },
        onError: (error: any) => {
          showMessage(error.response?.data?.message || 'Failed to delete module', 'error');
        },
      });
    }
  };

  const columns: GridColDef<Module>[] = [
    {
      field: 'name',
      headerName: 'Module Name',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.key}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      minWidth: 300,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 180,
      renderCell: (params) => (
        <Chip
          label={getCategoryLabel(params.value)}
          size="small"
          sx={{
            backgroundColor: getCategoryColor(params.value) + '20',
            color: getCategoryColor(params.value),
            fontWeight: 600,
            borderColor: getCategoryColor(params.value),
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        />
      ),
    },
    {
      field: 'tierMinimum',
      headerName: 'Tier',
      width: 150,
      renderCell: (params) => {
        const tierMinimum = params.value as string | null;
        const isAddOn = params.row.isAddOn;

        if (isAddOn) {
          return (
            <Chip
              icon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
              label="Add-on"
              size="small"
              sx={{
                backgroundColor: '#9C27B020',
                color: '#9C27B0',
                fontWeight: 600,
                '& .MuiChip-icon': { color: '#9C27B0' },
              }}
            />
          );
        }

        if (!tierMinimum) {
          return (
            <Chip
              label="Core"
              size="small"
              sx={{
                backgroundColor: '#4CAF5020',
                color: '#4CAF50',
                fontWeight: 600,
              }}
            />
          );
        }

        const tierColors: Record<string, string> = {
          free: '#6B7280',        // gray (matches TierBadge)
          starter: '#3B82F6',     // blue
          professional: '#8B5CF6', // purple
          enterprise: '#F59E0B',  // gold
        };
        const tierLabels: Record<string, string> = {
          free: 'Free',
          starter: 'Starter',
          professional: 'Professional',
          enterprise: 'Enterprise',
        };
        const color = tierColors[tierMinimum] || '#757575';

        return (
          <Chip
            icon={<WorkspacePremiumIcon sx={{ fontSize: 16 }} />}
            label={tierLabels[tierMinimum] || tierMinimum}
            size="small"
            sx={{
              backgroundColor: color + '20',
              color: color,
              fontWeight: 600,
              '& .MuiChip-icon': { color: color },
            }}
          />
        );
      },
    },
    {
      field: 'dependencies',
      headerName: 'Requires',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => <ModuleDependenciesCell moduleKey={params.row.key} />,
    },
    {
      field: 'dependents',
      headerName: 'Required By',
      flex: 1,
      minWidth: 220,
      renderCell: (params) => <ModuleDependentsCell moduleKey={params.row.key} />,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => navigate(`/subscriptions/modules/edit/${params.row.id}`)}
            title="Edit module"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDeleteClick(params.row.id)}
            title="Delete module"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Feature Modules</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Manage feature modules and capabilities
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/subscriptions/modules/create')}
        >
          Create Module
        </Button>
      </Box>

      <Card>
        <CardContent>
          <DataGrid
            rows={modules || []}
            columns={columns}
            loading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: {
                sortModel: [{ field: 'category', sort: 'asc' }],
              },
            }}
            autoHeight
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            sx={{
              '& .MuiDataGrid-cell': {
                py: 1.5,
              },
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Module</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this module? This action cannot be undone.
            Subscription plans and organizations using this module may be affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
